// R1b-δ — Chain integrity + break-authorizations.
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../../components/ui/dialog";
import { toast } from "sonner";
import StatusChip from "../../../components/StatusChip";
import { CheckCircle2, XCircle, ShieldAlert } from "lucide-react";

export default function AuditChainVerificationTab({ isOwner }) {
  const [state, setState] = useState(null);
  const [breaks, setBreaks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ hash_chain_index: "", reason: "", evidence_note: "" });

  const verify = async () => {
    try {
      const r = await api.post("/audit/verify-chain");
      setState(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadBreaks = async () => {
    try {
      const r = await api.get("/audit/break-authorizations");
      setBreaks(r.data.items || r.data || []);
    } catch (_) { setBreaks([]); }
  };
  useEffect(() => { verify(); loadBreaks(); }, []);

  const authorize = async (e) => {
    e.preventDefault();
    try {
      await api.post("/audit/authorize-break", {
        hash_chain_index: parseInt(form.hash_chain_index, 10),
        reason: form.reason,
        evidence: form.evidence_note ? { note: form.evidence_note } : null,
      });
      toast.success("Break authorized · audit-logged");
      setOpen(false);
      setForm({ hash_chain_index: "", reason: "", evidence_note: "" });
      verify(); loadBreaks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="audit-chain-verification-body">
      <div className="mb-4">
        <div className="font-display font-semibold text-erf-dark text-lg">Chain Verification</div>
        <div className="text-xs font-nav text-erf-dark/60 mt-0.5">Live cryptographic chain integrity + authorized breaks.</div>
      </div>

      {state && (
        <div
          className="rounded-md border p-4 mb-4"
          style={{
            borderColor: state.ok ? "rgba(22,163,74,0.4)" : "rgba(220,38,38,0.4)",
            background: state.ok ? "rgba(22,163,74,0.05)" : "rgba(220,38,38,0.05)",
          }}
          data-testid="chain-verify-state"
          role="status"
        >
          <div className="flex items-center gap-2 mb-2">
            {state.ok
              ? <CheckCircle2 className="w-5 h-5 text-green-700" />
              : <XCircle className="w-5 h-5 text-red-700" />}
            <span className={`font-display font-semibold ${state.ok ? "text-green-800" : "text-red-800"}`} data-testid="chain-verify-ok">
              {state.ok ? "Chain OK" : "Chain broken"}
            </span>
          </div>
          <div className="grid gap-2 md:grid-cols-4 text-[11px] font-mono">
            <div><span className="text-erf-dark/60">total_entries:</span> {state.total_entries}</div>
            <div><span className="text-erf-dark/60">verified:</span> {state.verified}</div>
            <div><span className="text-erf-dark/60">legacy_unverified:</span> {state.legacy_unverified}</div>
            <div><span className="text-erf-dark/60">authorized_breaks:</span> [{(state.authorized_breaks || []).join(", ")}]</div>
            {!state.ok && (
              <>
                <div><span className="text-erf-dark/60">broken_at:</span> {state.broken_at}</div>
                <div className="md:col-span-3"><span className="text-erf-dark/60">reason:</span> {state.first_broken_reason}</div>
              </>
            )}
          </div>
          <div className="mt-3">
            <Button size="sm" variant="outline" onClick={verify} data-testid="chain-verify-run">Re-run verification</Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-semibold text-erf-dark">Authorized Break Registry</div>
        {isOwner && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="chain-authorize-break-btn">
                <ShieldAlert className="w-4 h-4 mr-1" /> Authorize break
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-erf-cream">
              <DialogHeader><DialogTitle className="font-display">Authorize chain break</DialogTitle></DialogHeader>
              <form onSubmit={authorize} className="space-y-3">
                <div className="text-[11px] font-nav text-erf-dark/60">This inserts a new authorization row and a companion audit-ledger entry. It does NOT alter existing entries.</div>
                <div>
                  <Label className="text-xs uppercase font-nav">Hash chain index</Label>
                  <Input type="number" required value={form.hash_chain_index} onChange={(e) => setForm({...form, hash_chain_index: e.target.value})} className="mt-1 font-mono" data-testid="authorize-break-index" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav">Reason (≥12 chars)</Label>
                  <Textarea required minLength={12} value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} className="mt-1" data-testid="authorize-break-reason" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav">Evidence note (optional)</Label>
                  <Input value={form.evidence_note} onChange={(e) => setForm({...form, evidence_note: e.target.value})} className="mt-1" data-testid="authorize-break-evidence" />
                </div>
                <Button type="submit" className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="authorize-break-submit">Authorize</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
        {!isOwner && (
          <div className="text-[11px] font-mono text-erf-dark/60" data-testid="authorize-break-admin-note">Owner-only action</div>
        )}
      </div>

      <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
        <table className="w-full erf-table">
          <thead className="bg-erf-creamSecondary">
            <tr>
              <th className="text-left px-3 py-2">Index</th>
              <th className="text-left px-3 py-2">Reason</th>
              <th className="text-left px-3 py-2">Authorized by</th>
              <th className="text-left px-3 py-2">Authorized at</th>
            </tr>
          </thead>
          <tbody>
            {breaks.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-[11px] font-mono text-erf-dark/60">No authorized breaks on file.</td></tr>
            )}
            {breaks.map((b) => (
              <tr key={b.hash_chain_index} className="border-t border-erf-borderCream/60" data-testid={`break-row-${b.hash_chain_index}`}>
                <td className="px-3 py-2 mono">{b.hash_chain_index}</td>
                <td className="px-3 py-2 font-nav text-[12px]">{b.reason}</td>
                <td className="px-3 py-2 mono text-[10px]">{(b.authorized_by || "").slice(0, 8) || "—"}</td>
                <td className="px-3 py-2 mono text-[10px]">{b.authorized_at ? new Date(b.authorized_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
